<template>
  <div class="trends-report-dt-table-chart">
    <div
      :id="chartID"
      class="trends-report-dt-table-chart__chart-card"
      @click="showModal = !showModal"
    ></div>
    <BaseModal
      v-if="showModal"
      color="grey"
      rounded
      :size="fromApp ? 'app' : 'tr-modal'"
      :evenPadding="!fromApp"
      @background-clicked="hideModal"
    >
      <div class="trends-report-dt-table-chart__modal">
        <div
          :class="
            fromApp
              ? 'trends-report-dt-table-chart__modal-close mobile'
              : 'trends-report-dt-table-chart__modal-close'
          "
          @click="hideModal"
        >
          <span>X</span>
        </div>
        <div
          :class="
            fromApp
              ? 'trends-report-dt-table-chart__title mobile'
              : 'trends-report-dt-table-chart__title'
          "
        >
          <h1>{{ rowData.CompanyName }}</h1>
        </div>
        <div v-if="fromApp" class="trends-report-dt-table-chart__graph">
          <TrendsReportDTChart
            :dtData="rowData.DataTrendsChart"
            :border="true"
            :width="deviceWidth"
            :fromReport="true"
          />
        </div>
        <div v-else class="trends-report-dt-table-chart__graph">
          <TrendsReportDTChart
            :dtData="rowData.DataTrendsChart"
            :border="true"
            :isGlobal="isGlobal"
          />
        </div>
        <div
          :class="
            fromApp
              ? 'trends-report-dt-table-chart__metric mobile'
              : 'trends-report-dt-table-chart__metric'
          "
        >
          {{ metricText }}
        </div>
        <div
          :class="
            fromApp
              ? 'trends-report-dt-table-chart__link mobile'
              : 'trends-report-dt-table-chart__link'
          "
          @click="viewReport"
          v-if="!isGlobal"
        >
          <p>Read Full Article</p>
          <img class="pop-out" :src="getPopOutPath" />
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
  import BaseModal from '~/components/base/BaseModal.vue';
  import TrendsReportDTChart from '~/components/trendsreport/TrendsReportDTChart.vue';

  export default {
    name: 'TrendsReportDTTableChart',
    components: {
      BaseModal,
      TrendsReportDTChart,
    },
    props: {
      height: {
        type: String,
        default: () => {
          return '50';
        },
      },
      width: {
        type: String,
        default: () => {
          return '80';
        },
      },
      rowData: {
        type: Object,
        required: true,
      },
      isGlobal: {
        type: Boolean,
        default: false,
      },
      fromApp: {
        type: Boolean,
        default: false,
      },
      imagespath: {
        type: String,
        required: false,
      },
    },
    data: () => ({
      chartLayout: {},
      primaryHistoricDataTrace: {},
      chartID: (Math.random() + 1).toString(36).substring(3),
      modalID: (Math.random() + 1).toString(36).substring(3),
      showModal: false,
      deviceWidth: 0,
    }),
    mounted() {
      this.buildChartData();
      if (this.fromApp) {
        this.deviceWidth = window.innerWidth - 92;
      }
    },

    methods: {
      buildChartData() {
        let monthYear = [];
        let primaryHistoricData = [];
        for (
          let index = 0;
          index < this.rowData.DataTrendsChart.PrimaryHistoricData.length;
          index++
        ) {
          const element =
            this.rowData.DataTrendsChart.PrimaryHistoricData[index];
          monthYear.push(element.MonthYear);
          primaryHistoricData.push(element.DataPoint);
        }

        if (!primaryHistoricData.length) {
          for (
            let index = 0;
            index < this.rowData.DataTrendsChart.SecondaryHistoricData.length;
            index++
          ) {
            const element =
              this.rowData.DataTrendsChart.SecondaryHistoricData[index];
            monthYear.push(element.MonthYear);
            primaryHistoricData.push(element.DataPoint);
          }
        }
        this.primaryHistoricDataTrace = {
          type: 'scatter',
          mode: 'scatter',
          x: monthYear,
          y: primaryHistoricData,
          name: this.rowData.DataTrendsChart.PrimaryMetric,
          line: {
            width: 0.5,
            color: '#012249',
          },
          fill: 'tozeroy',
          fillcolor: '#012249',
          yaxis: 'y1',
          xaxis: 'x',
          visible: true,
        };

        this.chartLayout = {
          showlegend: false,
          autosize: true,
          dragmode: false,
          hovermode: false,
          yaxis: {
            title: '',
            showline: false,
            zeroLine: true,
            autotick: true,
            showgrid: false,
            ticks: '',
            showticklabels: false,
          },
          xaxis: {
            visible: false,
          },
          yaxis2: {
            title: '',
            zeroline: false,
            autotick: true,
            automargin: true,
            showgrid: false,
            showline: true,
            ticks: 'outside',
            side: 'right',
            overlaying: 'y',
          },
          width: this.width,
          height: this.height,
          margin: {
            t: 0,
            l: 2,
            r: 2,
            b: 1,
          },
        };

        this.plotChart();
      },
      plotChart() {
        Plotly.react(
          this.chartID,
          [this.primaryHistoricDataTrace],
          this.chartLayout,
          {
            responsive: true,
            staticPlot: true,
            displaylogo: false,
            displayModeBar: false,
          }
        ).then((chartData) => {
          this.adjustChart();
        });
      },
      adjustChart(chartData = null) {
        let layoutObject = this.getTableRanges();
        Plotly.update(this.chartID, null, layoutObject);
      },
      getTableRanges: function () {
        let y1_min = this.rowData.DataTrendsChart.MinY1;
        let y1_max = this.rowData.DataTrendsChart.MaxY1;
        let y2_min = this.rowData.DataTrendsChart.MinY2;
        let y2_max = this.rowData.DataTrendsChart.MaxY2;

        let ticks = this.getTicks(y1_min, y1_max, y2_min, y2_max);
        if (y1_min < 0) {
          y1_min = 0;
        }
        if (y2_min < 0) {
          y2_min = 0;
        }

        return {
          'yaxis.range': [y1_min, y1_max],
          'yaxis2.range': [y2_min, y2_max],
          'yaxis.dtick': ticks.y1dTick,
          'yaxis2.dtick': ticks.y2dTick,
          'yaxis.tickformat': `.${this.rowData.DataTrendsChart.DTDecimalPrecision}f`,
          'yaxis2.tickformat': `.${this.rowData.DataTrendsChart.DTDecimalPrecision}f`,
          'xaxis.tickfont': { size: this.fontSize },
          'yaxis.tickfont': { size: this.fontSize },
          'yaxis2.tickfont': { size: this.fontSize },
          'yaxis.title.font': { size: this.fontSize },
          'yaxis2.title.font': { size: this.fontSize },
          'yaxis.ticklabelposition': 'outside', // Default value
          'yaxis2.ticklabelposition': 'outside', // Default value
        };
      },
      getTicks: function (y1_min, y1_max, y2_min, y2_max) {
        const GRIDLINES = 5;

        var y1_range;
        if (y1_min < 0) {
          y1_range = y1_max - y1_min;
        } else {
          y1_range = y1_max;
        }

        y1_range = y1_range * 1000; // mult by 1000 to account for ranges < 1
        let y1_len = Math.floor(y1_range).toString().length;
        let y1_pow10_divisor = Math.pow(10, y1_len - 1);
        let y1_firstdigit = Math.floor(y1_range / y1_pow10_divisor);
        let y1_max_base = (y1_pow10_divisor * y1_firstdigit) / 1000; // div by 1000 to account for ranges < 1
        let y1_dtick = y1_max_base / GRIDLINES;

        y1_pow10_divisor = Math.pow(10, y1_len - 1) / 1000; // reset for logging purposes
        y1_range = y1_range / 1000; // range reset

        var y2_range;
        if (y2_min < 0) {
          y2_range = y2_max - y2_min;
        } else {
          y2_range = y2_max;
        }

        y2_range = y2_range * 1000; // mult by 1000 to account for ranges < 1
        let y2_len = Math.floor(y2_range).toString().length;

        let y2_pow10_divisor = Math.pow(10, y2_len - 1);
        let y2_firstdigit = Math.floor(y2_range / y2_pow10_divisor);
        let y2_max_base = (y2_pow10_divisor * y2_firstdigit) / 1000; // div by 1000 to account for ranges < 1

        let y2_dtick = y2_max_base / GRIDLINES;

        y2_pow10_divisor = Math.pow(10, y2_len - 1) / 1000; // reset for logging purposes
        y2_range = y2_range / 1000; // range reset

        /**************************************************************************/
        // Capture the highest dtick ratio as your global dtick ratio.
        //
        // All other axes will have their positive and negative ranges scaled to
        // make their dtick_ratios match the global ratio. When the ratios match,
        // the gridlines match!
        /**************************************************************************/

        var y1_dtick_ratio = y1_range / y1_dtick;
        var y2_dtick_ratio = y2_range / y2_dtick;

        var global_dtick_ratio = Math.max(y1_dtick_ratio, y2_dtick_ratio);

        /**************************************************************************/
        // Calculate Range Minimums
        //
        // 1. This is done by first finding the negative ratio for all axes:
        //     1. what percentage of the range is coming from negative values
        //     2. multiply percentage by global ratio to get the percentage of the
        //        global ratio (percentage of total gridlines) that should be shown
        //        under the zero baseline.
        //
        //     NEGATIVE RATIO == NUMBER OF GRIDLINES NEEDED FOR NEGATIVE VALUES
        //
        // 2. Capturing the highest negative ratio as the global negative ratio
        //
        // 3. Then applying the negative ratio to all of your axis minimumsto get
        //    their new proportionally scaled range minimums
        /**************************************************************************/

        var negative = false; // Are there any negative values present
        var y1_negative_ratio;
        if (y1_min < 0) {
          negative = true;
          y1_negative_ratio = Math.abs(y1_min / y1_range) * global_dtick_ratio;
        } else {
          y1_negative_ratio = 0;
        }

        var y2_negative_ratio;
        if (y2_min < 0) {
          negative = true;
          y2_negative_ratio = Math.abs(y2_min / y2_range) * global_dtick_ratio;
        } else {
          y2_negative_ratio = 0;
        }

        // Increase the ratio by 0.1 so that your range minimums are extended just
        // far enough to not cut off any part of your lowest value
        var global_negative_ratio =
          Math.max(y1_negative_ratio, y2_negative_ratio) + 0.1;

        // If any negative value is present, you must proportionally extend the
        // range minimum of all axes
        var y1_range_min;
        var y2_range_min;
        if (negative) {
          y1_range_min = global_negative_ratio * y1_dtick * -1;
          y2_range_min = global_negative_ratio * y2_dtick * -1;
        } else {
          // If no negatives, baseline is set to zero
          y1_range_min = 0;
          y2_range_min = 0;
        }

        // ************************************************************************
        // Calculate Range Maximums
        //
        // 1. This is done by first finding the positive ratio for all axes:
        //     1. what percentage of the range is coming from positive values
        //     2. multiply percentage by global ratio to get the percentage of the
        //        global ratio (percentage of total gridlines) that should be shown
        //        above the zero baseline.
        //
        //     POSITIVE RATIO == NUMBER OF GRIDLINES NEEDED FOR POSITIVE VALUES
        //
        // 2. Capturing the highest positive ratio as the global positive ratio
        //
        // 3. Then applying the positive ratio to all of your axis maximums to get
        //    their new proportionally scaled range maximums
        /**************************************************************************/

        var y1_positive_ratio =
          Math.abs(y1_max / y1_range) * global_dtick_ratio;
        var y2_positive_ratio =
          Math.abs(y2_max / y2_range) * global_dtick_ratio;

        // Increase the ratio by 0.1 so that your range maximums are extended just
        // far enough to not cut off any part of your highest value
        var global_positive_ratio =
          Math.max(y1_positive_ratio, y2_positive_ratio) + 0.1;

        var y1_range_max = global_positive_ratio * y1_dtick;
        var y2_range_max = global_positive_ratio * y2_dtick;

        return {
          y1Range: [y1_range_min, y1_range_max],
          y1dTick: y1_dtick,
          y2Range: [y2_range_min, y2_range_max],
          y2dTick: y2_dtick,
        };
      },
      hideModal() {
        this.showModal = false;
      },
      viewReport() {
        window.location.href = `${this.rowData.Link}/${this.rowData.TREditionID}`;
      },
    },
    computed: {
      metricText() {
        const { CurrentDTValue, DataTrendsChart, Symbol } = this.rowData;
        const { DTDecimalPrecision, SecondaryMetric } = DataTrendsChart;

        const roundedValue =
          parseFloat(CurrentDTValue).toFixed(DTDecimalPrecision);

        const formattedValue = `${Symbol}${roundedValue}`;

        return `Current ${SecondaryMetric}: ${formattedValue}`;
      },
      getPopOutPath() {
        if (this.imagespath && this.imagespath.length > 0) {
          return `${this.imagespath}/images/pop-out-blue.png`;
        }
        return '/images/pop-out-blue.png';
      },
    },
  };
</script>

<style lang="scss" scoped>
  .trends-report-dt-table-chart {
    display: flex;
    align-content: center;
    justify-content: center;
    align-items: center;

    &__chart-card {
      &:hover {
        cursor: pointer;
        box-shadow:
          0 4px 8px 0 rgba(0, 0, 0, 0.2),
          0 6px 20px 0 rgba(0, 0, 0, 0.19);
      }
    }
    &__modal {
      // position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
    }
    &__modal-close {
      color: var(--itr-white);
      font-size: 18px;
      font-weight: 700;
      height: 26px;
      width: 26px;
      border-radius: 50%;
      background: var(--itr-blue);
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 12px;
      right: 12px;
      cursor: pointer;
      &.mobile {
        font-size: 10px;
        height: 18px;
        width: 18px;
      }
    }
    &__link {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      p {
        color: #000000 !important;
      }
      .pop-out {
        color: var(--itr-blue);
        margin-left: 8px;
      }
      &.mobile {
        font-size: 14px;
      }
    }
    &__metric {
      margin: 2rem 0 3rem 0;
      color: var(--itr-blue);
      font-size: 24px;
      font-weight: bold;
      text-align: center;
      &.mobile {
        font-size: 13px;
        margin: 14px 0 16px 0;
      }
    }
    &__title {
      text-align: center;
      h1 {
        line-height: 1.2;
      }
      &.mobile {
        h1 {
          font-size: 18px;
        }
      }
    }
  }
</style>
